import React, { type CSSProperties, type FC } from "react";
import type { TerminalChildrenModeButtonShape } from "../../../../types/Admin/TerminalAppearanceTypes";
import {
  DEFAULT_CHILDREN_PRESET_THEME_ID,
  getChildrenModePresetTheme,
} from "../../../../utils/Terminal/childrenModeThemes";
import type { ChildrenModeIconMeta } from "./themes/ChildrenModeCardSpace";
import { ChildrenModeCardSpace } from "./themes/ChildrenModeCardSpace";
import { ChildrenModeCardJungle } from "./themes/ChildrenModeCardJungle";
import { ChildrenModeCardSea } from "./themes/ChildrenModeCardSea";
import { ChildrenModeCardFairyForest } from "./themes/ChildrenModeCardFairyForest";
import { ChildrenModeCardHeroCity } from "./themes/ChildrenModeCardHeroCity";
import { ChildrenModeCardKidsBase } from "./themes/ChildrenModeCardKidsBase";
import { ChildrenModeSpaceGreeting } from "./themes/ChildrenModeSpaceGreeting";
import { ChildrenModeJungleGreeting } from "./themes/ChildrenModeJungleGreeting";
import { ChildrenModeSeaGreeting } from "./themes/ChildrenModeSeaGreeting";
import { ChildrenModeFairyForestGreeting } from "./themes/ChildrenModeFairyForestGreeting";
import { ChildrenModeHeroCityGreeting } from "./themes/ChildrenModeHeroCityGreeting";

export type ChildrenModeThemeGreetingProps = {
  title: string;
  subTitle: string;
};

export type ChildrenModeThemeCardProps = {
  isCategory: boolean;
  isService: boolean;
  isActiveNow: boolean;
  shouldHideOuterCircle: boolean;
  ticketCircleStyle?: CSSProperties;
  ticketName: string;
  isChildrenMode: boolean;
  childrenButtonShape?: TerminalChildrenModeButtonShape;
  childrenIconMeta: ChildrenModeIconMeta | null;
  childrenTileColor?: string;
  childrenTileTextColor?: string;
  planetHueRotate?: number;
};

const CHILDREN_MODE_THEME_COMPONENTS: Record<
  string,
  FC<ChildrenModeThemeCardProps>
> = {
  space: ChildrenModeCardSpace,
  jungle: ChildrenModeCardJungle,
  sea: ChildrenModeCardSea,
  "fairy-forest": ChildrenModeCardFairyForest,
  "hero-city": ChildrenModeCardHeroCity,
};

const CHILDREN_MODE_THEME_GREETING_COMPONENTS: Record<
  string,
  FC<ChildrenModeThemeGreetingProps>
> = {
  space: ChildrenModeSpaceGreeting,
  jungle: ChildrenModeJungleGreeting,
  sea: ChildrenModeSeaGreeting,
  "fairy-forest": ChildrenModeFairyForestGreeting,
  "hero-city": ChildrenModeHeroCityGreeting,
};

export const resolveChildrenModeThemeId = (presetThemeId?: string | null) =>
  getChildrenModePresetTheme(presetThemeId).id;

const ChildrenModeCardIndividual: FC<ChildrenModeThemeCardProps> = ({
  childrenButtonShape,
  ...props
}) => (
  <ChildrenModeCardKidsBase
    {...props}
    childrenButtonShape={childrenButtonShape ?? "RECTANGLE"}
  />
);

export const getChildrenModeThemeComponent = (
  presetThemeId?: string | null,
  isIndividualTheme = false,
): FC<ChildrenModeThemeCardProps> => {
  if (isIndividualTheme) {
    return ChildrenModeCardIndividual;
  }

  const themeId = resolveChildrenModeThemeId(presetThemeId);
  return (
    CHILDREN_MODE_THEME_COMPONENTS[themeId] ??
    CHILDREN_MODE_THEME_COMPONENTS[DEFAULT_CHILDREN_PRESET_THEME_ID]
  );
};

export const getChildrenModeThemeGreetingComponent = (
  presetThemeId?: string | null,
  isIndividualTheme = false,
): FC<ChildrenModeThemeGreetingProps> | null => {
  if (isIndividualTheme) {
    return null;
  }

  const themeId = resolveChildrenModeThemeId(presetThemeId);
  return CHILDREN_MODE_THEME_GREETING_COMPONENTS[themeId] ?? null;
};

export const isSpaceChildrenTheme = (presetThemeId?: string | null) =>
  resolveChildrenModeThemeId(presetThemeId) === "space";

export const isPresetChildrenTheme = (presetThemeId?: string | null) =>
  resolveChildrenModeThemeId(presetThemeId) in CHILDREN_MODE_THEME_COMPONENTS;
